import { useEffect, useState } from 'react';
import moment from 'moment';
import { UserCircleIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

import useAsync from '../../hooks/useAsync';
import { getEntityAuditTrail } from '../../api';
import { Badge, StackableModal } from '../nextgen';

// Action type to badge styles
const actionStyles = {
	CREATE: 'success',
	UPDATE: 'warning',
	DELETE: 'danger',
	PUBLISH: 'info',
	APPROVE: 'success',
};

// Format value for display
const formatValue = (value) => {
	if (value === null || value === undefined) return '(empty)';
	if (typeof value === 'boolean') return value ? 'true' : 'false';
	if (Array.isArray(value)) {
		if (value.length === 0) return '(empty list)';
		// For arrays of objects (like steps), show count
		if (typeof value[0] === 'object') {
			return `${value.length} item${value.length !== 1 ? 's' : ''}`;
		}
		return value.join(', ');
	}
	if (typeof value === 'object') {
		// Try to get a name or meaningful display
		if (value.name) return value.name;
		return JSON.stringify(value);
	}
	// Truncate long strings
	const str = String(value);
	if (str.length > 100) return str.slice(0, 100) + '...';
	return str;
};

// Strip HTML tags for cleaner display
const stripHtml = (html) => {
	if (!html) return '';
	return html.replace(/<[^>]*>/g, '').trim();
};

const SopAuditLog = ({ sopId }) => {
	const [selectedEntry, setSelectedEntry] = useState(null);

	const {
		value: auditData,
		execute: fetchAuditTrail,
		status,
	} = useAsync(getEntityAuditTrail, { immediate: false });

	useEffect(() => {
		if (sopId) {
			fetchAuditTrail({ entityType: 'sop', entityId: sopId, limit: 50 });
		}
	}, [sopId]);

	const isLoading = status === 'pending';
	const entries = auditData || [];

	// Render changes diff
	const renderChanges = (changes) => {
		if (!changes || Object.keys(changes).length === 0) {
			return (
				<span className="text-gray-400 text-sm italic">No changes recorded</span>
			);
		}

		return (
			<div className="space-y-3">
				{Object.entries(changes).map(([field, change]) => {
					// Format field name
					const fieldLabel = field
						.replace(/([A-Z])/g, ' $1')
						.replace(/^./, (s) => s.toUpperCase())
						.trim();

					// Handle HTML content (like description)
					const fromValue =
						field === 'description'
							? stripHtml(change.from)
							: formatValue(change.from);
					const toValue =
						field === 'description'
							? stripHtml(change.to)
							: formatValue(change.to);

					return (
						<div key={field} className="text-sm">
							<span className="font-medium text-gray-700">{fieldLabel}</span>
							<div className="mt-1 flex flex-col gap-1 ml-3">
								<div className="flex items-start gap-2">
									<span className="text-xs text-gray-400 w-12 flex-shrink-0">
										From:
									</span>
									<span className="text-red-600 bg-red-50 px-2 py-0.5 rounded text-xs break-all">
										{fromValue}
									</span>
								</div>
								<div className="flex items-start gap-2">
									<span className="text-xs text-gray-400 w-12 flex-shrink-0">
										To:
									</span>
									<span className="text-green-600 bg-green-50 px-2 py-0.5 rounded text-xs break-all">
										{toValue}
									</span>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
				<span className="ml-3 text-gray-500">Loading audit log...</span>
			</div>
		);
	}

	if (!entries.length) {
		return (
			<div className="text-center py-12">
				<div className="text-gray-400 mb-2">
					<svg
						className="mx-auto h-12 w-12"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1.5}
							d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
						/>
					</svg>
				</div>
				<p className="text-gray-500 text-sm">No audit history yet</p>
				<p className="text-gray-400 text-xs mt-1">
					Changes to this SOP will be logged here
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-2">
			<p className="text-sm text-gray-500 mb-4">
				{entries.length} change{entries.length !== 1 ? 's' : ''} recorded
			</p>

			<div className="border border-gray-200 rounded-lg overflow-hidden">
				{entries.map((entry, idx) => (
					<div
						key={entry.id}
						className={`${idx !== 0 ? 'border-t border-gray-200' : ''}`}
					>
						{/* Entry row - clickable */}
						<div
							onClick={() => setSelectedEntry(entry)}
							className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
						>
							{/* Timestamp */}
							<div className="flex-shrink-0 w-32">
								<div className="text-sm text-gray-900">
									{moment(entry.createdAt).format('MMM D, YYYY')}
								</div>
								<div className="text-xs text-gray-500">
									{moment(entry.createdAt).format('h:mm A')}
								</div>
							</div>

							{/* Action badge */}
							<div className="flex-shrink-0">
								<Badge variant={actionStyles[entry.action] || 'muted'}>
									{entry.action}
								</Badge>
							</div>

							{/* Actor */}
							<div className="flex-1 flex items-center gap-2 min-w-0">
								<UserCircleIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
								{entry.actorName ? (
									<div className="truncate">
										<span className="text-sm text-gray-900">
											{entry.actorName}
										</span>
										{entry.actorEmail && (
											<span className="text-xs text-gray-500 ml-1">
												({entry.actorEmail})
											</span>
										)}
									</div>
								) : (
									<span className="text-sm text-gray-400 italic">System</span>
								)}
							</div>

							{/* Signature indicator for PUBLISH */}
							{entry.action === 'PUBLISH' && entry.metadata?.signatureData && (
								<div className="flex-shrink-0">
									<span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
										<PencilSquareIcon className="w-3 h-3" />
										Signed
									</span>
								</div>
							)}

							{/* Changes count */}
							{entry.changes && Object.keys(entry.changes).length > 0 && (
								<div className="flex-shrink-0 text-xs text-gray-500">
									{Object.keys(entry.changes).length} field
									{Object.keys(entry.changes).length !== 1 ? 's' : ''} changed
								</div>
							)}
						</div>
					</div>
				))}
			</div>

			{/* Audit Entry Detail Modal */}
			<StackableModal
				open={!!selectedEntry}
				onClose={() => setSelectedEntry(null)}
				title="Audit Entry Details"
				size="3xl"
			>
				{selectedEntry && (
					<div className="space-y-6">
						{/* Header Info */}
						<div className="grid grid-cols-2 gap-4 pb-4 border-b border-gray-200">
							<div>
								<p className="text-sm text-gray-500">Timestamp</p>
								<p className="font-medium">
									{moment(selectedEntry.createdAt).format(
										'MMM D, YYYY h:mm:ss A',
									)}
								</p>
							</div>
							<div>
								<p className="text-sm text-gray-500">Action</p>
								<Badge variant={actionStyles[selectedEntry.action] || 'muted'}>
									{selectedEntry.action}
								</Badge>
							</div>
							<div>
								<p className="text-sm text-gray-500">Entity</p>
								<p className="font-medium">
									{selectedEntry.entityName || 'Unnamed'}
								</p>
								<p className="text-xs text-gray-400 font-mono">
									{selectedEntry.entityId}
								</p>
							</div>
							<div>
								<p className="text-sm text-gray-500">Actor</p>
								{selectedEntry.actorName ? (
									<>
										<p className="font-medium">{selectedEntry.actorName}</p>
										<p className="text-sm text-gray-500">
											{selectedEntry.actorEmail}
										</p>
									</>
								) : (
									<p className="text-gray-400">System</p>
								)}
							</div>
						</div>

						{/* E-Signature Section for PUBLISH */}
						{selectedEntry.action === 'PUBLISH' &&
							selectedEntry.metadata?.signatureData && (
								<div className="p-4 bg-indigo-50 rounded-lg border border-indigo-100">
									<div className="flex items-center gap-2 mb-3">
										<PencilSquareIcon className="w-5 h-5 text-indigo-600" />
										<h4 className="font-medium text-gray-900">E-Signature</h4>
									</div>
									<div className="flex items-start gap-6">
										{/* Signature Image */}
										<div className="flex-shrink-0">
											<div className="border border-indigo-200 rounded-lg p-3 bg-white">
												<img
													src={selectedEntry.metadata.signatureData}
													alt="E-Signature"
													className="h-20 w-auto"
												/>
											</div>
										</div>
										{/* Signature Details */}
										<div className="flex-1 text-sm space-y-2">
											{selectedEntry.metadata.signerName && (
												<div>
													<span className="text-gray-500">Signed by: </span>
													<span className="text-gray-900 font-medium">
														{selectedEntry.metadata.signerName}
													</span>
												</div>
											)}
											{selectedEntry.metadata.signerEmail && (
												<div>
													<span className="text-gray-500">Email: </span>
													<span className="text-gray-900">
														{selectedEntry.metadata.signerEmail}
													</span>
												</div>
											)}
											{selectedEntry.metadata.signedAt && (
												<div>
													<span className="text-gray-500">Signed at: </span>
													<span className="text-gray-900">
														{moment(selectedEntry.metadata.signedAt).format(
															'MMMM D, YYYY [at] h:mm:ss A',
														)}
													</span>
												</div>
											)}
											{selectedEntry.metadata.version && (
												<div>
													<span className="text-gray-500">Version: </span>
													<span className="text-gray-900 font-medium">
														v{selectedEntry.metadata.version}
													</span>
												</div>
											)}
										</div>
									</div>
								</div>
							)}

						{/* Changes */}
						<div>
							<h4 className="font-medium text-gray-900 mb-3">Changes</h4>
							{renderChanges(selectedEntry.changes)}
						</div>

						{/* Context */}
						{(selectedEntry.ipAddress ||
							selectedEntry.requestId ||
							(selectedEntry.metadata &&
								Object.keys(selectedEntry.metadata).length > 0 &&
								selectedEntry.action !== 'PUBLISH')) && (
							<div>
								<h4 className="font-medium text-gray-900 mb-3">Context</h4>
								<div className="space-y-2 text-sm">
									{selectedEntry.ipAddress && (
										<div>
											<span className="text-gray-500">IP Address:</span>{' '}
											<span className="font-mono">
												{selectedEntry.ipAddress}
											</span>
										</div>
									)}
									{selectedEntry.requestId && (
										<div>
											<span className="text-gray-500">Request ID:</span>{' '}
											<span className="font-mono text-xs">
												{selectedEntry.requestId}
											</span>
										</div>
									)}
									{selectedEntry.metadata &&
										Object.keys(selectedEntry.metadata).length > 0 &&
										selectedEntry.action !== 'PUBLISH' && (
											<div>
												<span className="text-gray-500">Metadata:</span>
												<pre className="mt-1 text-xs bg-gray-50 p-3 rounded border overflow-auto max-h-40">
													{JSON.stringify(selectedEntry.metadata, null, 2)}
												</pre>
											</div>
										)}
								</div>
							</div>
						)}

						{/* Previous/New State */}
						{(selectedEntry.previousState || selectedEntry.newState) && (
							<div>
								<h4 className="font-medium text-gray-900 mb-3">
									State Snapshots
								</h4>
								<div className="grid grid-cols-2 gap-4">
									{selectedEntry.previousState && (
										<div>
											<p className="text-sm text-gray-500 mb-1">
												Previous State
											</p>
											<pre className="text-xs bg-red-50 p-3 rounded border border-red-100 overflow-auto max-h-48">
												{JSON.stringify(selectedEntry.previousState, null, 2)}
											</pre>
										</div>
									)}
									{selectedEntry.newState && (
										<div>
											<p className="text-sm text-gray-500 mb-1">New State</p>
											<pre className="text-xs bg-green-50 p-3 rounded border border-green-100 overflow-auto max-h-48">
												{JSON.stringify(selectedEntry.newState, null, 2)}
											</pre>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				)}
			</StackableModal>
		</div>
	);
};

export default SopAuditLog;
